import { isSetupDeferred } from '@/helpers/guidedSetup'
import { useBusinessOptions } from '@/services/businessOptions'
import { affectsWorkspace } from '@/services/workspaceRefresh'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Alert, Button, Form, InputGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClipboardList, faFileInvoice, faNoteSticky } from '@fortawesome/free-solid-svg-icons'
import { AdminPageHero } from '@/components/common/AdminPageHero'
import { CustomerSearchDialog } from '@/components/customers/CustomerSearchDialog'
import { CustomerTaskDialog } from '@/components/customers/CustomerTaskDialog'
import { useCrmHub } from '@/context/SignalRContext'
import { useAuth } from '@/context/AuthContext'
import { editorRoles, ownerRole } from '@/helpers/roles'
import { readError } from '@/services/apiClient'
import { loadActions } from '@/services/actionService'
import type { CustomerResult } from '@/services/customerService'

type ActionCounts = { open: number; overdue: number; today: number }

async function loadCounts(assignedToId?: string): Promise<ActionCounts> {
  const [open, overdue, today] = await Promise.all([
    loadActions({ assignedToId, showFor: 'All', pageSize: 1 }),
    loadActions({ assignedToId, showFor: 'Overdue', pageSize: 1 }),
    loadActions({ assignedToId, showFor: 'Today', pageSize: 1 }),
  ])
  return { open: open.total, overdue: overdue.total, today: today.total }
}

export function DashboardPage() {
  const setup = useBusinessOptions()
  const { session } = useAuth()
  const { lastEvent } = useCrmHub()
  const business = session?.businesses.find((item) => item.businessId === session.businessId)
  const navigate = useNavigate()
  const owner = session?.role === ownerRole
  const canEdit = !!session?.role && editorRoles.includes(session.role)
  const [mine, setMine] = useState<ActionCounts | null>(null)
  const [everyone, setEveryone] = useState<ActionCounts | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [task, setTask] = useState<'order' | 'action' | 'note' | null>(null)
  const [compose, setCompose] = useState<{ kind: 'action' | 'note'; customer: CustomerResult } | null>(null)
  const [countsRevision, setCountsRevision] = useState(0)
  const [customerQuery, setCustomerQuery] = useState('')
  const firstName = session?.fullName?.trim().split(/\s+/)[0]

  useEffect(() => {
    if (affectsWorkspace(lastEvent, 'dashboard')) setCountsRevision(value => value + 1)
  }, [lastEvent])

  useEffect(() => {
    if (!session?.businessUserId) return
    let cancelled = false
    setError(null)
    const requests = [loadCounts(session.businessUserId)]
    if (owner) requests.push(loadCounts())
    Promise.all(requests)
      .then(([personal, businessWide]) => {
        if (cancelled) return
        setMine(personal)
        setEveryone(businessWide ?? null)
      })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [session?.businessUserId, owner, countsRevision])

  useEffect(() => {
    if (!isSetupDeferred(session?.businessId ?? '') && !setup.loading && setup.account?.onboardingCompleted === false && session?.role && ['CRMAccountOwner', 'CRMAccountAdmin'].includes(session.role)) navigate('/setup', { replace: true })
  }, [setup.loading, setup.account?.onboardingCompleted, session?.businessId, session?.role, navigate])

  return (
    <>
      <AdminPageHero
        title={firstName ? `Hi ${firstName}` : 'Your CRM workspace'}
        summary={business?.businessName || 'Your CRM workspace'}
      >
        <Link className="btn btn-primary" to="/actions/mine">My actions</Link>
        <Link className="btn btn-outline-primary" to="/actions/team">All actions</Link>
      </AdminPageHero>
      {setup.account?.onboardingCompleted === false && session?.role && ['CRMAccountOwner', 'CRMAccountAdmin'].includes(session.role) && <Alert variant="info">Your CRM setup plan is ready. <Link to="/setup">Continue setup</Link></Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      <section className="admin-panel mb-3">
        <div className="dashboard-search-row">
          <Form
            className="dashboard-search-row__find"
            onSubmit={(event) => {
              event.preventDefault()
              const query = customerQuery.trim()
              navigate(query ? `/customers?q=${encodeURIComponent(query)}` : '/customers')
            }}
          >
            <Form.Label>Find a customer</Form.Label>
            <InputGroup className="customer-search-bar">
              <Form.Control
                value={customerQuery}
                placeholder="Name, postcode, account number or contact"
                aria-label="Find a customer"
                onChange={(event) => setCustomerQuery(event.target.value)}
              />
              <Button type="submit">Search</Button>
            </InputGroup>
          </Form>
          {canEdit && <Link className="btn btn-success" to="/customers/new">Add customer</Link>}
        </div>
      </section>
      <ActionTotals title="Your actions" counts={mine} base="/actions/mine" />
      {owner && <ActionTotals title="All users" counts={everyone} base="/actions/team" quiet />}
      {canEdit && (
        <section className="admin-feature-grid mt-3">
          <button className="dashboard-task" type="button" onClick={() => setTask('order')}>
            <FontAwesomeIcon icon={faClipboardList} />
            New submission
          </button>
          <button className="dashboard-task" type="button" onClick={() => setTask('action')}>
            <FontAwesomeIcon icon={faFileInvoice} />
            Add action
          </button>
          <button className="dashboard-task" type="button" onClick={() => setTask('note')}>
            <FontAwesomeIcon icon={faNoteSticky} />
            Add note
          </button>
        </section>
      )}
      {task && (
        <CustomerSearchDialog
          title={task === 'order' ? 'New submission' : task === 'action' ? 'Add action' : 'Add note'}
          confirmLabel={task === 'order' ? 'New submission' : task === 'action' ? 'Add action' : 'Add note'}
          onClose={() => setTask(null)}
          onSelect={(customer: CustomerResult) => {
            if (task === 'order') {
              setTask(null)
              navigate(`/orders/new/${customer.id}`)
              return
            }
            if (task === 'action' || task === 'note') setCompose({ kind: task, customer })
            setTask(null)
          }}
        />
      )}
      {compose && (
        <CustomerTaskDialog
          kind={compose.kind}
          customer={compose.customer}
          onClose={() => setCompose(null)}
          onSaved={() => { if (compose.kind === 'action') setCountsRevision((value) => value + 1) }}
        />
      )}
    </>
  )
}

function ActionTotals({ title, counts, base, quiet = false }: { title: string; counts: ActionCounts | null; base: string; quiet?: boolean }) {
  const value = (count: number | undefined) => counts ? String(count ?? 0) : '—'
  const cards = [
    { label: 'Open actions', count: counts?.open, to: base, tone: '' },
    { label: 'Overdue actions', count: counts?.overdue, to: `${base}?due=Overdue`, tone: 'admin-stat-card--danger' },
    { label: "Today's actions", count: counts?.today, to: `${base}?due=Today`, tone: 'admin-stat-card--success' },
  ]
  return (
    <section className={`mb-3 ${quiet ? 'admin-totals--quiet' : ''}`}>
      <h2 className="h6 text-secondary mb-2">{title}</h2>
      <div className="admin-stats-grid">
        {cards.map((card) => (
          <Link className={`admin-stat-card ${card.tone}`} to={card.to} key={card.label}>
            <div className="admin-stat-card__title">{card.label}</div>
            <div className="admin-stat-card__value">{value(card.count)}</div>
          </Link>
        ))}
      </div>
    </section>
  )
}
